import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Bell, Send, Users, User } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

export default function NotificationsPage() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    title: '',
    message: '',
    type: 'broadcast', // broadcast | individual
    user_id: ''
  });

  useEffect(() => {
    loadUsers();
    loadHistory();
  }, []);

  const loadUsers = async () => {
    try {
      const { data } = await api.get('/api/admin/users');
      setUsers(data.filter(u => u._id !== currentUser?.id));
    } catch (error) {
      toast.error('Failed to load users');
    }
  };

  const loadHistory = async () => {
    try {
      const { data } = await api.get('/api/admin/notifications/history');
      setHistory(data);
    } catch (error) {
      toast.error('Failed to load history');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.message.trim()) {
      toast.error('Title and message are required');
      return;
    }

    if (form.type === 'individual' && !form.user_id) {
      toast.error('Please select a user');
      return;
    }

    setLoading(true);
    try {
      if (form.type === 'broadcast') {
        const { data } = await api.post('/api/admin/notifications/broadcast', {
          title: form.title,
          message: form.message
        });
        toast.success(`Broadcast sent to ${data.recipients_count} users`);
      } else {
        const { data } = await api.post('/api/admin/notifications/send', {
          title: form.title,
          message: form.message,
          user_id: form.user_id
        });
        toast.success(`Notification sent to ${data.recipient}`);
      }

      setForm({ title: '', message: '', type: 'broadcast', user_id: '' });
      loadHistory();
    } catch (error) {
      toast.error('Failed to send notification');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="notifications-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight font-['Outfit']">Notifications</h1>
        <p className="text-sm text-muted-foreground mt-1">Send notifications to users</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Send Notification Form */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Send className="h-5 w-5" />
              Send Notification
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Notification Type</Label>
                <Select 
                  value={form.type} 
                  onValueChange={(v) => setForm({ ...form, type: v, user_id: '' })}
                >
                  <SelectTrigger data-testid="notification-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="broadcast">
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4" />
                        Broadcast to All Users
                      </div>
                    </SelectItem>
                    <SelectItem value="individual">
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4" />
                        Individual User
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {form.type === 'individual' && (
                <div className="space-y-2">
                  <Label>Select User</Label>
                  <Select value={form.user_id} onValueChange={(v) => setForm({ ...form, user_id: v })}>
                    <SelectTrigger data-testid="user-select">
                      <SelectValue placeholder="Choose a user..." />
                    </SelectTrigger>
                    <SelectContent>
                      {users.map((u) => (
                        <SelectItem key={u._id} value={u._id}>
                          {u.name} ({u.username})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-2">
                <Label>Title</Label>
                <Input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="Notification title..."
                  maxLength={100}
                  data-testid="notification-title"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label>Message</Label>
                <Textarea
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="Notification message..."
                  rows={4}
                  maxLength={500}
                  data-testid="notification-message"
                  required
                />
                <p className="text-xs text-muted-foreground text-right">
                  {form.message.length}/500
                </p>
              </div>

              <Button type="submit" disabled={loading} className="w-full" data-testid="send-notification-btn">
                {loading ? 'Sending...' : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Send {form.type === 'broadcast' ? 'Broadcast' : 'to User'}
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Notification History */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5" />
              Notification History
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 max-h-[500px] overflow-y-auto">
              {history.length === 0 ? (
                <div className="text-center py-8 text-sm text-muted-foreground">
                  <Bell className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  No notifications sent yet
                </div>
              ) : (
                history.map((item, idx) => (
                  <div key={idx} className="p-3 border rounded-md space-y-2" data-testid={`history-${idx}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h4 className="font-semibold text-sm">{item.title}</h4>
                          {item.is_broadcast ? (
                            <Badge variant="outline" className="text-[10px] h-5">
                              <Users className="h-3 w-3 mr-1" />
                              Broadcast
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-[10px] h-5">
                              <User className="h-3 w-3 mr-1" />
                              Individual
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">{item.message}</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between text-[10px] text-muted-foreground">
                      <span>
                        {formatDistanceToNow(new Date(item.created_at), { addSuffix: true })}
                      </span>
                      <span>
                        {item.recipients_count} recipient{item.recipients_count !== 1 ? 's' : ''}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
